import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, Plus, Trash2, Edit2, Save, Layers } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { useAdmin } from '@/contexts/AdminContext';
import { toast } from 'sonner';

export default function CategoryManager({ siteKey, onClose }) {
  const { apiFetch } = useAdmin();
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingId, setEditingId] = useState(null);
  const [editName, setEditName] = useState('');
  const [newName, setNewName] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) loadCategories();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, siteKey]);

  const loadCategories = () => {
    setLoading(true);
    apiFetch(`/categories?siteKey=${siteKey}`)
      .then(r => r.json())
      .then(d => setCategories(d.categories || []))
      .catch(() => toast.error('Failed to load categories'))
      .finally(() => setLoading(false));
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!newName.trim()) return;
    setSaving(true);
    try {
      const res = await apiFetch('/categories', {
        method: 'POST',
        body: { siteKey, name: newName.trim() }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to create category');
      }
      setNewName('');
      toast.success('Category added');
      loadCategories();
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleUpdate = async (id) => {
    if (!editName.trim()) return;
    try {
      const res = await apiFetch(`/categories/${id}`, {
        method: 'PUT',
        body: { name: editName.trim() }
      });
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.error || 'Failed to update category');
      }
      setEditingId(null);
      toast.success('Category updated');
      loadCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('Are you sure you want to delete this category?')) return;
    try {
      const res = await apiFetch(`/categories/${id}`, { method: 'DELETE' });
      if (!res.ok) throw new Error('Failed to delete category');
      toast.success('Category deleted');
      loadCategories();
    } catch (err) {
      toast.error(err.message);
    }
  };

  return (
    <>
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-40 bg-black/20" onClick={onClose} />
      <motion.div 
        initial={{ x: '100%' }} animate={{ x: 0 }} exit={{ x: '100%' }} transition={{ type: 'spring', damping: 25, stiffness: 200 }}
        className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-white shadow-2xl border-l border-stone-200 z-50 flex flex-col"
      >
        <div className="p-6 border-b border-stone-200 flex items-center justify-between bg-stone-50/50">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-full bg-stone-200 flex items-center justify-center">
              <Layers className="w-5 h-5 text-stone-600" />
            </div>
            <div>
              <h3 className="text-xl font-playfair font-medium text-stone-900">Categories</h3>
              <p className="text-sm text-stone-500">Manage categories for this site</p>
            </div>
          </div>
          <button onClick={onClose} className="h-8 w-8 rounded-full bg-white border border-stone-200 flex items-center justify-center hover:bg-stone-50">
            <X className="w-4 h-4 text-stone-500" />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          <form onSubmit={handleCreate} className="flex gap-2">
            <Input 
              value={newName} 
              onChange={e => setNewName(e.target.value)} 
              placeholder="New category name..." 
              className="flex-1"
            />
            <Button type="submit" disabled={saving || !newName.trim()} className="bg-stone-900 hover:bg-stone-800 text-white shrink-0">
              <Plus className="w-4 h-4" />
            </Button>
          </form>

          {loading ? (
            <div className="space-y-3">
              <div className="h-12 bg-stone-100 rounded animate-pulse" />
              <div className="h-12 bg-stone-100 rounded animate-pulse" />
            </div>
          ) : categories.length === 0 ? (
            <div className="text-center py-8 text-stone-500 text-sm">
              No categories found. Create one above.
            </div>
          ) : (
            <ul className="space-y-2">
              {categories.map(c => (
                <li key={c.id} className="group flex items-center justify-between p-3 rounded-lg border border-stone-200 bg-white hover:border-stone-300 transition-colors">
                  {editingId === c.id ? (
                    <div className="flex items-center gap-2 flex-1">
                      <Input value={editName} onChange={e => setEditName(e.target.value)} className="h-8 text-sm" autoFocus />
                      <Button size="sm" onClick={() => handleUpdate(c.id)} className="h-8 bg-stone-900 text-white">Save</Button>
                      <Button size="sm" variant="ghost" onClick={() => setEditingId(null)} className="h-8 text-stone-500">Cancel</Button>
                    </div>
                  ) : (
                    <>
                      <span className="font-medium text-stone-800 text-sm">{c.name}</span>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-stone-400 hover:text-stone-900" onClick={() => { setEditingId(c.id); setEditName(c.name); }}>
                          <Edit2 className="w-4 h-4" />
                        </Button>
                        <Button size="sm" variant="ghost" className="h-8 w-8 p-0 text-red-400 hover:text-red-600 hover:bg-red-50" onClick={() => handleDelete(c.id)}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </motion.div>
    </>
  );
}
